import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { TitleStrategy } from '@angular/router';
import {
  NgxCurrencyInputMode,
  provideEnvironmentNgxCurrency,
} from 'ngx-currency';
import { DateFnsModule } from 'ngx-date-fns';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';

import { POLICY_ICON_MAP } from '@lockton-apps/client-gateway/shared';
import { DynamicFormsModule } from '@lockton-apps/dynamic-forms';
import { IconModule } from '@lockton-apps/icon';
import {
  AUTH_CONFIG,
  AuthInterceptor,
  SharedDataAccessAuthModule,
} from '@lockton-apps/shared/data-access/auth';
import { SharedDataAccessAuthUsersModule } from '@lockton-apps/shared/data-access/auth-user-selectors';
import { SharedDataAccessClientsModule } from '@lockton-apps/shared/data-access/clients';
import { SharedDataAccessContactsModule } from '@lockton-apps/shared/data-access/contacts';
import { SharedDataAccessCoreModule } from '@lockton-apps/shared/data-access/core';
import { SharedDataAccessDocumentsModule } from '@lockton-apps/shared/data-access/documents';
import { SharedDataAccessFoldersModule } from '@lockton-apps/shared/data-access/folders';
import { SharedDataAccessPoliciesModule } from '@lockton-apps/shared/data-access/policies';
import { SharedDataAccessPortalConfigModule } from '@lockton-apps/shared/data-access/portal-config';
import { SharedDataAccessT2CodesModule } from '@lockton-apps/shared/data-access/t2-codes';
import { SharedDataAccessUsersModule } from '@lockton-apps/shared/data-access/users';
import { LOQATE_CONFIG } from '@lockton-apps/shared/ui/lui';
import { provideApplicationInsightsMonitoring } from '@lockton-apps/shared/util/app-monitoring';
import {
  LSOS_BASE_PATH,
  PROPOSALS_BASE_PATH,
  PROPOSALS_MOCK_IDS,
  PROPOSALS_MOCKS,
} from '@lockton-apps/shared/util/base-path';
import { provideErrorHandling } from '@lockton-apps/shared/util/error-handling';
import { provideCoreValidationMessages } from '@lockton-apps/shared/util/form-utils';
import { provideLocalization } from '@lockton-apps/shared/util/localization';
import { provideOneTrust } from '@lockton-apps/shared/util/onetrust';
import { provideStripe, StripeModule } from '@lockton-apps/shared/util/stripe';
import { ClientPortalTitleStrategy } from '@lockton-apps/shared/util/title-strategy';
import { UPLOAD_CONFIG } from '@lockton-apps/shared/util/upload-config';
import { provideUserIdle } from '@lockton-apps/shared/util/user-idle';
import {
  filePickerCallback,
  TINYMCE_EDITOR_OPTIONS,
} from '@lockton-apps/shared/ui/presentation-builder';

import { authConfig } from '../config/auth-config';
import { ACCEPTED_DOCUMENT_FILE_TYPES } from '../static';
import { policyIconMap } from '../static/policies';
import {
  DYNAMIC_TABLE_CELLS_CORE,
  provideDynamicTableCells,
} from '@lockton-apps/dynamic-table';
import { provideFreshDesk } from '@lockton-apps/shared/util/freshdesk';
import {
  isExperimental,
  provideInitExperimental,
} from '@lockton-apps/shared/util/helpers';
import { environment } from '../../environments/environment';
import {
  Application,
  APPLICATION,
  COB_META_DATA_LATAM,
  COUNTRY_NAMES,
  COUNTRY_NAMES_LATAM,
  ENABLE_FCA_CLASSIFICATION,
  NAV_DEFINITION,
  NAV_DEFINITION_LATAM,
  provideCobMeta,
  TEMP_COB_DATA,
  TEMP_COB_DATA_LATAM,
} from '@lockton-apps/shared/util/constants';
import { sortBy } from 'lodash';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from '@lockton-apps/material';
import { AuthUserIdleService } from '@lockton-apps/shared/util/auth-user-idle';
import { MessagesDataAccessModule } from '@lockton-apps/shared/data-access/messages';
import { proposalsMocksLatam } from '@lockton-apps/shared/util/proposal-utils';
import { provideLayoutConfig } from '@lockton-apps/core/feature/layout';

const uploadConfig = {
  acceptedFileTypes: [...ACCEPTED_DOCUMENT_FILE_TYPES],
  acceptedFileTypesDescription: [
    'WORD',
    'EXCEL',
    'CSV',
    'JPEG',
    'PNG',
    'PDF',
    'POWERPOINT',
    'MSG',
  ].join(', '),
};

@NgModule({
  declarations: [],
  imports: [
    BrowserAnimationsModule,
    SharedDataAccessAuthModule,
    SharedDataAccessT2CodesModule,
    SharedDataAccessContactsModule,
    SharedDataAccessUsersModule,
    SharedDataAccessPoliciesModule,
    SharedDataAccessDocumentsModule,
    SharedDataAccessFoldersModule,
    SharedDataAccessClientsModule,
    SharedDataAccessAuthUsersModule,
    SharedDataAccessPortalConfigModule,
    CommonModule,
    DateFnsModule.forRoot(),
    HttpClientModule,
    IconModule.forRoot({ path: 'assets/ui/icons' }),
    StripeModule,
    SharedDataAccessCoreModule.forRoot(environment.production),
    DynamicFormsModule,
    MessagesDataAccessModule,
  ],
  providers: [
    provideInitExperimental({
      allowExperimental: environment.features.allowExperimental,
      experimentalByDefault: environment.features.experimentalByDefault,
    }),
    // TODO: provide one base api url and add version inside each service
    {
      provide: 'BASE_API_URL',
      useValue: environment.apiUrl,
    },
    {
      provide: 'BASE_API_URL_V2',
      useValue: environment.apiUrlV2,
    },
    {
      provide: 'APP_URL',
      useValue: environment.appUrl,
    },
    {
      provide: APPLICATION,
      useValue: Application.Latam,
    },
    //feature:portalConfig-integration remove all instances in the file
    {
      provide: 'FEATURE_PORTALCONFIG',
      useValue: environment.features.portalConfig,
    },
    // feature:proposalDocuments-integration remove all instances in the file
    {
      provide: 'FEATURE_PROPOSAL_DOCUMENTS',
      useValue: environment.features.proposalDocuments,
    },
    // feature:newClientEnquiriesForExistingClients remove all instances in the file
    {
      provide: 'FEATURE_NEW_ENQUIRIES_FOR_EXISTING_CLIENTS',
      useValue: environment.features.newClientEnquiriesForExistingClients,
    },
    {
      provide: LSOS_BASE_PATH,
      useValue: '/lsos',
    },
    {
      provide: PROPOSALS_BASE_PATH,
      useValue: '/proposals',
    },
    {
      provide: 'BASE_MEDIA_URL',
      useValue: environment.mediaUrl,
    },
    {
      provide: PROPOSALS_MOCK_IDS,
      useFactory: () => (isExperimental() ? environment.useMocksFor : []),
    },
    {
      provide: PROPOSALS_MOCKS,
      useValue: proposalsMocksLatam,
    },
    {
      provide: COUNTRY_NAMES,
      useValue: sortBy(COUNTRY_NAMES_LATAM),
    },
    {
      provide: ENABLE_FCA_CLASSIFICATION,
      useValue: false,
    },
    {
      provide: NAV_DEFINITION,
      useValue: NAV_DEFINITION_LATAM,
    },
    {
      provide: 'AZURE_STORAGE_ACCOUNT_NAME',
      useValue: environment.storageAccountName,
    },
    {
      provide: UPLOAD_CONFIG,
      useValue: uploadConfig,
    },
    { provide: AUTH_CONFIG, useValue: authConfig },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { showDelay: 300 } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        width: '100%',
        maxWidth: 640,
        autoFocus: 'first-tabbable',
        role: 'dialog',
        restoreFocus: true,
      },
    },
    {
      provide: LOQATE_CONFIG,
      useValue: {
        serviceKey: environment.loqateConfig.serviceKey,
        findUrl: environment.loqateConfig.findUrl,
        retrieveUrl: environment.loqateConfig.retrieveUrl,
        findLabel: $localize`Address`,
        retrieveLabel: $localize`Multiple addresses at this location`,
        findErrorMessage: $localize`Please select an address`,
        retrieveErrorMessage: $localize`Please confirm your address`,
        placeholder: $localize`Start typing address or postcode`,
      },
    },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    provideNgxMask(),
    {
      provide: POLICY_ICON_MAP,
      useValue: policyIconMap,
    },
    { provide: TitleStrategy, useClass: ClientPortalTitleStrategy },
    { provide: Window, useValue: window },
    NgxMaskPipe,
    // Provide common validation messages
    provideCoreValidationMessages({
      default: {
        required: $localize`This field is required`,
        email: $localize`Please enter a valid email address`,
        password: $localize`Please enter a valid password`,
        passwordConfirm: $localize`Passwords do not match`,
        numberChars: $localize`Password must contain at least { n } number(s)`,
        onlyPasswordChars: $localize`Password contains an invalid character`,
        specialChars: $localize`Password must contain at least { n } special character(s)`,
        matching: $localize`Passwords do not match`,
        minlength: $localize`Password must be a minimum of { requiredLength } characters long`,
        lowercaseChars: $localize`Password must contain at least { n } lowercase character(s)`,
        uppercaseChars: $localize`Password must contain at least { n } uppercase character(s)`,
      },
    }),
    provideUserIdle(),
    provideStripe(environment.stripeKey),
    provideErrorHandling(),
    provideEnvironmentNgxCurrency({
      align: 'left',
      allowNegative: false,
      decimal: '.',
      prefix: '',
      thousands: ',',
      inputMode: NgxCurrencyInputMode.Natural,
    }),
    provideOneTrust({
      disabled: environment.oneTrustConfig.disabled,
      domainScript: environment.oneTrustConfig.domainScript,
    }),
    provideLocalization({
      devMode: environment.name === 'dev' || environment.name === 'mock',
      defaultCurrencyCode: 'USD', // TODO: not sure what default should be
    }),
    provideApplicationInsightsMonitoring({
      connectionString: environment.appInsights.connectionString,
      enableAutoRouteTracking: true,
      disableTelemetry: environment.appInsights.disableTelemetry,
    }),
    {
      provide: TINYMCE_EDITOR_OPTIONS,
      useValue: {
        apiKey: environment.tinymceKey,
        plugins: 'lists image link table pagebreak',
        menubar: '',
        toolbar:
          'undo redo | blocks | bold italic underline strikethrough | align bullist numlist | table | image | pagebreak',
        options: {
          file_picker_callback: filePickerCallback,
          menubar: false,
          block_formats:
            'Body=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4;',
        },
      },
    },
    { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
    provideDynamicTableCells(DYNAMIC_TABLE_CELLS_CORE),
    provideFreshDesk({
      disabled: environment.freshdeskConfig.disabled,
      widgetId: environment.freshdeskConfig.widgetId,
      environment: environment.freshdeskConfig.environment,
      messaging: {
        banner: $localize`Mediamaker Support`,
        launcher: $localize`Help`,
        contact_form: {
          title: $localize`Contact Us`,
          submit: $localize`Send`,
          confirmation: $localize`Thank you for your feedback!`,
        },
      },
    }),
    AuthUserIdleService,
    provideCobMeta(COB_META_DATA_LATAM),
    provideLayoutConfig({
      copyright: 'Lockton Companies',
    }),
    {
      provide: TEMP_COB_DATA,
      useValue: TEMP_COB_DATA_LATAM,
    },
  ],
})
export class CoreModule {}
